import { Plus, Trash2 } from 'lucide-react'

// ============================================
// Helpers
// ============================================
const Input = ({ label, value, onChange, placeholder, type = 'text', className = '' }: any) => (
  <div className={className}>
    {label && <label className="block text-xs font-bold text-gray-500 mb-1">{label}</label>}
    <input type={type} value={value || ''} onChange={e => onChange(type === 'number' ? Number(e.target.value) : e.target.value)}
      placeholder={placeholder} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none" />
  </div>
)

const TextArea = ({ label, value, onChange, placeholder, rows = 2, className = '' }: any) => (
  <div className={className}>
    {label && <label className="block text-xs font-bold text-gray-500 mb-1">{label}</label>}
    <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-300 focus:border-purple-400 outline-none" />
  </div>
)

const Checkbox = ({ label, checked, onChange }: any) => (
  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
    <input type="checkbox" checked={checked || false} onChange={e => onChange(e.target.checked)} className="rounded border-gray-300 text-purple-500 focus:ring-purple-400" />
    {label}
  </label>
)

const Select = ({ label, value, onChange, options, className = '' }: any) => (
  <div className={className}>
    {label && <label className="block text-xs font-bold text-gray-500 mb-1">{label}</label>}
    <select value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-300 outline-none">
      {options.map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
)

function ArrayManager<T>({ items, onChange, renderItem, newItem, addLabel = 'Agregar', max }: {
  items: T[]; onChange: (items: T[]) => void; renderItem: (item: T, index: number, update: (item: T) => void) => React.JSX.Element
  newItem: () => T; addLabel?: string; max?: number
}) {
  const add = () => { if (!max || items.length < max) onChange([...items, newItem()]) }
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i))
  const update = (i: number, item: T) => onChange(items.map((it, idx) => idx === i ? item : it))

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-start bg-white rounded-lg p-2.5 border border-gray-200">
          <span className="text-xs text-gray-500 font-mono mt-2 w-5 text-center flex-shrink-0">{i + 1}</span>
          <div className="flex-1">{renderItem(item, i, (updated) => update(i, updated))}</div>
          <button onClick={() => remove(i)} className="p-1 text-red-600 hover:text-red-600 mt-1 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button onClick={add} className="text-sm text-purple-600 font-medium hover:text-purple-800 flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </div>
  )
}

// ============================================
// Main dispatcher
// ============================================
export default function BlockConfigForm({ blockType, config, onChange }: {
  blockType: string; config: any; onChange: (config: any) => void
}) {
  const forms: Record<string, () => React.JSX.Element> = {
    title_slide: () => <TitleSlideForm config={config} onChange={onChange} />,
    vocabulary: () => <VocabularyForm config={config} onChange={onChange} />,
    phrase_list: () => <PhraseListForm config={config} onChange={onChange} />,
    dialogue: () => <DialogueForm config={config} onChange={onChange} />,
    roulette: () => <RouletteForm config={config} onChange={onChange} />,
    flashcards: () => <FlashcardsForm config={config} onChange={onChange} />,
    matching: () => <MatchingForm config={config} onChange={onChange} />,
    fill_blanks: () => <FillBlanksForm config={config} onChange={onChange} />,
    role_play: () => <RolePlayForm config={config} onChange={onChange} />,
    image_describe: () => <ImageDescribeForm config={config} onChange={onChange} />,
    poll: () => <PollForm config={config} onChange={onChange} />,
    timer_activity: () => <TimerActivityForm config={config} onChange={onChange} />,
    free_html: () => <FreeHtmlForm config={config} onChange={onChange} />,
    video_embed: () => <VideoEmbedForm config={config} onChange={onChange} />,
    audio_listen: () => <AudioListenForm config={config} onChange={onChange} />,
    sentence_builder: () => <SentenceBuilderForm config={config} onChange={onChange} />,
    conversation_prompts: () => <ConversationPromptsForm config={config} onChange={onChange} />,
  }
  const form = forms[blockType]
  if (!form) return <p className="text-gray-500 text-sm">Tipo no soportado: {blockType}</p>
  return form()
}

// ============================================
// 1. TITLE SLIDE
// ============================================
function TitleSlideForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  return (
    <div className="grid grid-cols-2 gap-3">
      <Input label="Título principal" value={config.heading} onChange={(v: string) => set('heading', v)} placeholder="Bienvenue !" className="col-span-2" />
      <Input label="Subtítulo" value={config.subheading} onChange={(v: string) => set('subheading', v)} placeholder="Primer contacto con el francés" className="col-span-2" />
      <Input label="Color de fondo" value={config.background_color} onChange={(v: string) => set('background_color', v)} placeholder="#1e3a5f" type="color" />
      <Checkbox label="Mostrar fecha" checked={config.show_date} onChange={(v: boolean) => set('show_date', v)} />
      <Input label="URL imagen (opcional)" value={config.image_url} onChange={(v: string) => set('image_url', v)} placeholder="https://..." className="col-span-2" />
    </div>
  )
}

// ============================================
// 2. VOCABULARY
// ============================================
function VocabularyForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  const words = config.words || []

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Input label="Título de sección" value={config.title} onChange={(v: string) => set('title', v)} placeholder="Vocabulaire du jour" className="col-span-2" />
        <Select label="Columnas" value={config.columns || 2} onChange={(v: string) => set('columns', Number(v))} options={[{ value: 1, label: '1 col' }, { value: 2, label: '2 cols' }, { value: 3, label: '3 cols' }]} />
      </div>
      <Checkbox label="Mostrar pronunciación fonética" checked={config.show_phonetic} onChange={(v: boolean) => set('show_phonetic', v)} />
      <span className="block text-xs font-bold text-gray-500">Palabras</span>
      <ArrayManager
        items={words}
        onChange={(w) => set('words', w)}
        newItem={() => ({ fr: '', es: '', phonetic: '', example: '' })}
        addLabel="Agregar palabra"
        renderItem={(word: any, _i, update) => (
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Français" value={word.fr} onChange={(v: string) => update({ ...word, fr: v })} />
            <Input placeholder="Español" value={word.es} onChange={(v: string) => update({ ...word, es: v })} />
            <Input placeholder="Fonética (bon-ZHUR)" value={word.phonetic} onChange={(v: string) => update({ ...word, phonetic: v })} />
            <Input placeholder="Ejemplo de uso" value={word.example} onChange={(v: string) => update({ ...word, example: v })} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 3. PHRASE LIST
// ============================================
function PhraseListForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const categories = config.categories || []
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })

  return (
    <div className="space-y-3">
      <Checkbox label="Resaltar palabras clave" checked={config.highlight_key_words} onChange={(v: boolean) => set('highlight_key_words', v)} />
      <span className="block text-xs font-bold text-gray-500">Categorías</span>
      {categories.map((cat: any, ci: number) => (
        <div key={ci} className="bg-[#0055A4]/5 rounded-lg p-3 border border-[#0055A4]/20 space-y-2">
          <div className="flex items-center gap-2">
            <Input placeholder="Nombre categoría" value={cat.name} onChange={(v: string) => {
              const updated = [...categories]; updated[ci] = { ...cat, name: v }; set('categories', updated)
            }} className="flex-1" />
            <button onClick={() => set('categories', categories.filter((_: any, i: number) => i !== ci))} className="p-1 text-red-600 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
          </div>
          <ArrayManager
            items={cat.phrases || []}
            onChange={(phrases) => { const updated = [...categories]; updated[ci] = { ...cat, phrases }; set('categories', updated) }}
            newItem={() => ({ fr: '', es: '', phonetic: '', context: '' })}
            addLabel="Agregar frase"
            renderItem={(phrase: any, _i, update) => (
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Français" value={phrase.fr} onChange={(v: string) => update({ ...phrase, fr: v })} />
                <Input placeholder="Español" value={phrase.es} onChange={(v: string) => update({ ...phrase, es: v })} />
                <Input placeholder="Fonética" value={phrase.phonetic} onChange={(v: string) => update({ ...phrase, phonetic: v })} />
                <Input placeholder="Contexto (formal, informal...)" value={phrase.context} onChange={(v: string) => update({ ...phrase, context: v })} />
              </div>
            )}
          />
        </div>
      ))}
      <button onClick={() => set('categories', [...categories, { name: '', phrases: [] }])} className="text-sm text-purple-600 font-medium hover:text-purple-800 flex items-center gap-1">
        <Plus className="w-3.5 h-3.5" /> Agregar categoría
      </button>
    </div>
  )
}

// ============================================
// 4. DIALOGUE
// ============================================
function DialogueForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  const characters = config.characters || []
  const lines = config.lines || []

  return (
    <div className="space-y-3">
      <Input label="Título del diálogo" value={config.title} onChange={(v: string) => set('title', v)} placeholder="Au café" />
      <Checkbox label="Mostrar traducción por defecto" checked={config.show_translation ?? true} onChange={(v: boolean) => set('show_translation', v)} />
      <span className="block text-xs font-bold text-gray-500">Personajes</span>
      <ArrayManager
        items={characters}
        onChange={(c) => set('characters', c)}
        newItem={() => ({ name: '', color: '#3b82f6' })}
        addLabel="Agregar personaje"
        max={4}
        renderItem={(char: any, _i, update) => (
          <div className="flex gap-2">
            <Input placeholder="Nombre" value={char.name} onChange={(v: string) => update({ ...char, name: v })} className="flex-1" />
            <input type="color" value={char.color || '#3b82f6'} onChange={e => update({ ...char, color: e.target.value })} className="w-10 h-9 rounded border border-gray-200 cursor-pointer" />
          </div>
        )}
      />
      <span className="block text-xs font-bold text-gray-500">Líneas del diálogo</span>
      <ArrayManager
        items={lines}
        onChange={(l) => set('lines', l)}
        newItem={() => ({ character: characters[0]?.name || '', fr: '', es: '' })}
        addLabel="Agregar línea"
        renderItem={(line: any, _i, update) => (
          <div className="grid grid-cols-3 gap-2">
            <Select value={line.character} onChange={(v: string) => update({ ...line, character: v })}
              options={characters.map((c: any) => ({ value: c.name, label: c.name }))} />
            <Input placeholder="Français" value={line.fr} onChange={(v: string) => update({ ...line, fr: v })} />
            <Input placeholder="Español" value={line.es} onChange={(v: string) => update({ ...line, es: v })} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 5. ROULETTE
// ============================================
function RouletteForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  const items = config.items || []

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Título" value={config.title} onChange={(v: string) => set('title', v)} placeholder="Ruleta de preguntas" />
        <Input label="Subtítulo" value={config.subtitle} onChange={(v: string) => set('subtitle', v)} placeholder="Gira y responde" />
      </div>
      <Input label="Duración giro (ms)" value={config.spin_duration_ms || 4000} onChange={(v: number) => set('spin_duration_ms', v)} type="number" />
      <span className="block text-xs font-bold text-gray-500">Opciones de la ruleta</span>
      <ArrayManager
        items={items}
        onChange={(it) => set('items', it)}
        newItem={() => ''}
        addLabel="Agregar opción"
        renderItem={(item: string, i, update) => (
          <Input placeholder={`Opción ${i + 1}`} value={item} onChange={(v: string) => update(v)} />
        )}
      />
    </div>
  )
}

// ============================================
// 6. FLASHCARDS
// ============================================
function FlashcardsForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })

  return (
    <div className="space-y-3">
      <div className="flex gap-4">
        <Checkbox label="Barajar" checked={config.shuffle} onChange={(v: boolean) => set('shuffle', v)} />
        <Checkbox label="Mostrar pistas" checked={config.show_hint} onChange={(v: boolean) => set('show_hint', v)} />
        <Select label="Layout" value={config.layout || 'grid'} onChange={(v: string) => set('layout', v)} options={[{ value: 'grid', label: 'Grilla' }, { value: 'stack', label: 'Apiladas' }]} />
      </div>
      <span className="block text-xs font-bold text-gray-500">Tarjetas</span>
      <ArrayManager
        items={config.cards || []}
        onChange={(c) => set('cards', c)}
        newItem={() => ({ front: '', back: '', hint: '' })}
        addLabel="Agregar tarjeta"
        renderItem={(card: any, _i, update) => (
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Frente (fr)" value={card.front} onChange={(v: string) => update({ ...card, front: v })} />
            <Input placeholder="Reverso (es)" value={card.back} onChange={(v: string) => update({ ...card, back: v })} />
            <Input placeholder="Pista (opcional)" value={card.hint} onChange={(v: string) => update({ ...card, hint: v })} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 7. MATCHING
// ============================================
function MatchingForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Etiqueta izquierda" value={config.left_label} onChange={(v: string) => set('left_label', v)} placeholder="Français" />
        <Input label="Etiqueta derecha" value={config.right_label} onChange={(v: string) => set('right_label', v)} placeholder="Español" />
      </div>
      <Checkbox label="Mezclar lados" checked={config.shuffle_sides} onChange={(v: boolean) => set('shuffle_sides', v)} />
      <span className="block text-xs font-bold text-gray-500">Pares</span>
      <ArrayManager
        items={config.pairs || []}
        onChange={(p) => set('pairs', p)}
        newItem={() => ({ left: '', right: '' })}
        addLabel="Agregar par"
        renderItem={(pair: any, _i, update) => (
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Izquierda (fr)" value={pair.left} onChange={(v: string) => update({ ...pair, left: v })} />
            <Input placeholder="Derecha (es)" value={pair.right} onChange={(v: string) => update({ ...pair, right: v })} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 8. FILL BLANKS
// ============================================
function FillBlanksForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })

  return (
    <div className="space-y-3">
      <div className="flex gap-4">
        <Checkbox label="Mostrar pistas" checked={config.show_hints} onChange={(v: boolean) => set('show_hints', v)} />
        <Select label="Modo revelación" value={config.reveal_mode || 'one_by_one'} onChange={(v: string) => set('reveal_mode', v)}
          options={[{ value: 'one_by_one', label: 'Una por una' }, { value: 'all_at_once', label: 'Todas a la vez' }]} />
      </div>
      <span className="block text-xs font-bold text-gray-500">Oraciones (usa ___ para el hueco)</span>
      <ArrayManager
        items={config.sentences || []}
        onChange={(s) => set('sentences', s)}
        newItem={() => ({ text: '', answer: '', hint: '' })}
        addLabel="Agregar oración"
        renderItem={(s: any, _i, update) => (
          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="Je ___ Marie." value={s.text} onChange={(v: string) => update({ ...s, text: v })} />
            <Input placeholder="Respuesta (suis)" value={s.answer} onChange={(v: string) => update({ ...s, answer: v })} />
            <Input placeholder="Pista" value={s.hint} onChange={(v: string) => update({ ...s, hint: v })} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 9. ROLE PLAY
// ============================================
function RolePlayForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })

  return (
    <div className="space-y-3">
      <TextArea label="Escenario (español)" value={config.scenario} onChange={(v: string) => set('scenario', v)} placeholder="Estás en la recepción de un hotel..." />
      <TextArea label="Escenario (francés)" value={config.scenario_fr} onChange={(v: string) => set('scenario_fr', v)} placeholder="Vous êtes à la réception..." />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Duración (minutos)" value={config.duration_minutes} onChange={(v: number) => set('duration_minutes', v)} type="number" />
        <Input label="Consejo" value={config.tips} onChange={(v: string) => set('tips', v)} placeholder="Usad vous para ser formal" />
      </div>
      <span className="block text-xs font-bold text-gray-500">Roles</span>
      <ArrayManager
        items={config.roles || []}
        onChange={(r) => set('roles', r)}
        newItem={() => ({ name: '', description: '', suggested_phrases: [] })}
        addLabel="Agregar rol"
        max={4}
        renderItem={(role: any, _i, update) => (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Nombre del rol" value={role.name} onChange={(v: string) => update({ ...role, name: v })} />
              <Input placeholder="Descripción" value={role.description} onChange={(v: string) => update({ ...role, description: v })} />
            </div>
            <TextArea placeholder="Frases sugeridas (una por línea)" value={(role.suggested_phrases || []).join('\n')}
              onChange={(v: string) => update({ ...role, suggested_phrases: v.split('\n').filter(Boolean) })} rows={2} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 10. IMAGE DESCRIBE
// ============================================
function ImageDescribeForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  return (
    <div className="space-y-3">
      <Input label="URL de la imagen" value={config.image_url} onChange={(v: string) => set('image_url', v)} placeholder="https://..." />
      <Input label="Descripción (caption)" value={config.caption} onChange={(v: string) => set('caption', v)} placeholder="Une famille au restaurant" />
      <TextArea label="Preguntas (una por línea)" value={(config.questions || []).join('\n')} onChange={(v: string) => set('questions', v.split('\n').filter(Boolean))} rows={3} />
      <TextArea label="Vocabulario de ayuda (una por línea)" value={(config.vocabulary_help || []).join('\n')} onChange={(v: string) => set('vocabulary_help', v.split('\n').filter(Boolean))} rows={2} />
    </div>
  )
}

// ============================================
// 11. POLL
// ============================================
function PollForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })

  return (
    <div className="space-y-3">
      <Input label="Pregunta" value={config.question} onChange={(v: string) => set('question', v)} placeholder="¿Cómo se dice...?" />
      <Checkbox label="Mostrar respuesta correcta" checked={config.show_correct ?? true} onChange={(v: boolean) => set('show_correct', v)} />
      <Input label="Explicación" value={config.explanation} onChange={(v: string) => set('explanation', v)} placeholder="En francés se usa..." />
      <span className="block text-xs font-bold text-gray-500">Opciones</span>
      <ArrayManager
        items={config.options || []}
        onChange={(o) => set('options', o)}
        newItem={() => ({ text: '', is_correct: false })}
        addLabel="Agregar opción"
        renderItem={(opt: any, _i, update) => (
          <div className="flex gap-2 items-center">
            <Input placeholder="Texto de la opción" value={opt.text} onChange={(v: string) => update({ ...opt, text: v })} className="flex-1" />
            <Checkbox label="Correcta" checked={opt.is_correct} onChange={(v: boolean) => update({ ...opt, is_correct: v })} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 12. TIMER ACTIVITY
// ============================================
function TimerActivityForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  return (
    <div className="space-y-3">
      <TextArea label="Actividad (español)" value={config.activity} onChange={(v: string) => set('activity', v)} placeholder="En parejas, practiquen saludos..." />
      <TextArea label="Actividad (francés)" value={config.activity_fr} onChange={(v: string) => set('activity_fr', v)} placeholder="En binômes, pratiquez..." />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Duración (segundos)" value={config.duration_seconds || 180} onChange={(v: number) => set('duration_seconds', v)} type="number" />
        <Checkbox label="Sonido al terminar" checked={config.sound_on_finish} onChange={(v: boolean) => set('sound_on_finish', v)} />
      </div>
      <TextArea label="Tips/frases de ayuda (uno por línea)" value={(config.tips || []).join('\n')} onChange={(v: string) => set('tips', v.split('\n').filter(Boolean))} rows={3} />
    </div>
  )
}

// ============================================
// 13. FREE HTML
// ============================================
function FreeHtmlForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  return (
    <TextArea label="Contenido HTML" value={config.html} onChange={(v: string) => onChange({ ...config, html: v })} placeholder="<div>...</div>" rows={8} />
  )
}

// ============================================
// 14. VIDEO EMBED
// ============================================
function VideoEmbedForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  return (
    <div className="space-y-3">
      <Input label="URL del video (YouTube o Drive)" value={config.url} onChange={(v: string) => set('url', v)} placeholder="https://youtube.com/watch?v=..." />
      <Input label="Título" value={config.title} onChange={(v: string) => set('title', v)} placeholder="Les salutations" />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Inicio (segundos)" value={config.start_seconds} onChange={(v: number) => set('start_seconds', v)} type="number" />
        <Input label="Fin (segundos)" value={config.end_seconds} onChange={(v: number) => set('end_seconds', v)} type="number" />
      </div>
      <TextArea label="Preguntas de discusión (una por línea)" value={(config.discussion_questions || []).join('\n')} onChange={(v: string) => set('discussion_questions', v.split('\n').filter(Boolean))} rows={3} />
    </div>
  )
}

// ============================================
// 15. AUDIO LISTEN
// ============================================
function AudioListenForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  return (
    <div className="space-y-3">
      <Input label="URL del audio" value={config.audio_url} onChange={(v: string) => set('audio_url', v)} placeholder="https://..." />
      <Input label="Título" value={config.title} onChange={(v: string) => set('title', v)} placeholder="Écoutez le dialogue" />
      <TextArea label="Transcripción (francés)" value={config.transcript_fr} onChange={(v: string) => set('transcript_fr', v)} rows={2} />
      <TextArea label="Transcripción (español)" value={config.transcript_es} onChange={(v: string) => set('transcript_es', v)} rows={2} />
      <Checkbox label="Mostrar transcripción por defecto" checked={config.show_transcript} onChange={(v: boolean) => set('show_transcript', v)} />
      <TextArea label="Preguntas (una por línea)" value={(config.questions || []).join('\n')} onChange={(v: string) => set('questions', v.split('\n').filter(Boolean))} rows={2} />
    </div>
  )
}

// ============================================
// 16. SENTENCE BUILDER
// ============================================
function SentenceBuilderForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  return (
    <div className="space-y-3">
      <Checkbox label="Mostrar traducción" checked={config.show_translation} onChange={(v: boolean) => set('show_translation', v)} />
      <span className="block text-xs font-bold text-gray-500">Oraciones</span>
      <ArrayManager
        items={config.sentences || []}
        onChange={(s) => set('sentences', s)}
        newItem={() => ({ words: [], correct_order: [], translation: '' })}
        addLabel="Agregar oración"
        renderItem={(s: any, _i, update) => (
          <div className="space-y-2">
            <Input label="Palabras (separadas por coma)" placeholder="m'appelle, Marie, Je"
              value={(s.words || []).join(', ')} onChange={(v: string) => update({ ...s, words: v.split(',').map((w: string) => w.trim()).filter(Boolean) })} />
            <Input label="Orden correcto (separadas por coma)" placeholder="Je, m'appelle, Marie"
              value={(s.correct_order || []).join(', ')} onChange={(v: string) => update({ ...s, correct_order: v.split(',').map((w: string) => w.trim()).filter(Boolean) })} />
            <Input label="Traducción" placeholder="Me llamo Marie" value={s.translation} onChange={(v: string) => update({ ...s, translation: v })} />
          </div>
        )}
      />
    </div>
  )
}

// ============================================
// 17. CONVERSATION PROMPTS
// ============================================
function ConversationPromptsForm({ config, onChange }: { config: any; onChange: (c: any) => void }) {
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Select label="Modo" value={config.mode || 'sequential'} onChange={(v: string) => set('mode', v)}
          options={[{ value: 'sequential', label: 'Secuencial' }, { value: 'random', label: 'Aleatorio' }]} />
        <Select label="Tipo" value={config.pairs_or_group || 'pairs'} onChange={(v: string) => set('pairs_or_group', v)}
          options={[{ value: 'pairs', label: 'En parejas' }, { value: 'group', label: 'En grupo' }]} />
        <Input label="Segundos por pregunta" value={config.time_per_prompt_seconds || 60} onChange={(v: number) => set('time_per_prompt_seconds', v)} type="number" />
      </div>
      <span className="block text-xs font-bold text-gray-500">Preguntas</span>
      <ArrayManager
        items={config.prompts || []}
        onChange={(p) => set('prompts', p)}
        newItem={() => ({ question_fr: '', question_es: '', model_answer: '', difficulty: 'easy' })}
        addLabel="Agregar pregunta"
        renderItem={(p: any, _i, update) => (
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Pregunta (francés)" value={p.question_fr} onChange={(v: string) => update({ ...p, question_fr: v })} />
            <Input placeholder="Pregunta (español)" value={p.question_es} onChange={(v: string) => update({ ...p, question_es: v })} />
            <Input placeholder="Respuesta modelo" value={p.model_answer} onChange={(v: string) => update({ ...p, model_answer: v })} />
            <Select value={p.difficulty || 'easy'} onChange={(v: string) => update({ ...p, difficulty: v })}
              options={[{ value: 'easy', label: 'Fácil' }, { value: 'medium', label: 'Intermedio' }, { value: 'hard', label: 'Difícil' }]} />
          </div>
        )}
      />
    </div>
  )
}
